import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import ProductCard from '../../components/ProductCard/ProductCard';
import CustomSelect from '../../components/CustomSelect/CustomSelect';
import './BestSeller.css';
import { fetchProducts } from '../../services/api';
import ProductCardSkeleton from '../../components/ProductCard/ProductCardSkeleton';

const heroImage = "https://res.cloudinary.com/dzqgh7xfd/image/upload/v1775365815/Banner_%C4%91%E1%BA%A7u_trang_zusklp.png";

const BestSeller = () => {
  const { t, i18n } = useTranslation();
  const [sortOption, setSortOption] = useState('default');
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const productsPerPage = 8;
  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const loadProducts = async () => {
      setLoading(true);

      const params = {
        is_bestseller: true,
        page: currentPage,
        page_size: productsPerPage,
      };

      if (searchQuery.trim()) {
        params.search = searchQuery;
      }

      if (sortOption === 'price-asc') {
        params.ordering = 'price';
      } else if (sortOption === 'price-desc') {
        params.ordering = '-price';
      } else if (sortOption === 'a-z') {
        params.ordering = 'name';
      }

      try {
        const data = await fetchProducts(params);
        if (!cancelled) {
          setProducts(data);
          setTotalCount(data?.length ?? 0);
        }
      } catch (err) {
        if (err.name !== 'AbortError' && !cancelled) {
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadProducts();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [sortOption, searchQuery, currentPage, i18n.language]);

  const totalPages = Math.ceil(totalCount / productsPerPage);

  return (
    <div className="best-seller-page bs-dark-theme">
      <div className="bs-hero">
        <img src={heroImage} alt="Best Seller" className="bs-banner-img" />
      </div>

      <div className="bs-container">

        {/* Top Control Bar */}
        <div className="bs-top-bar">
          <div className="bs-result-count"
            dangerouslySetInnerHTML={{ __html: t('bestSeller.resultCount', { count: totalCount }) }}
          />
          {/* Group phải: desktop = search+sort hàng ngang, mobile = 2 hàng */}
          <div className="bs-top-right">

            {/* Hang 1 (mobile): Loc + Sap xep */}
            <div className="bs-controls-row">
              <div className="bs-sort-control">
                <CustomSelect
                  value={sortOption}
                  onChange={(val) => { setSortOption(val); setCurrentPage(1); }}
                  options={[
                    { value: 'default', label: t('bestSeller.sortDefault') },
                    { value: 'a-z', label: t('bestSeller.sortAZ') },
                    { value: 'price-asc', label: t('bestSeller.sortPriceAsc') },
                    { value: 'price-desc', label: t('bestSeller.sortPriceDesc') },
                    { value: 'best-seller', label: t('bestSeller.sortBestSeller') },
                  ]}
                />
              </div>
            </div>

            {/* Hàng 2 (mobile) / inline (desktop): Tìm kiếm */}
            <div className="bs-search-box">
              <Search className="bs-search-icon" size={16} />
              <input
                type="text"
                placeholder={t('bestSeller.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

          </div>
        </div>

        {/* Main Layout */}
        <div className="bs-layout">

          {/* Product Grid */}
          <main className="bs-main-content">
            {loading ? (
              <div className="bs-grid">
                {Array.from({ length: productsPerPage }).map((_, i) => (
                  <div key={i} className="bs-grid-item">
                    <ProductCardSkeleton />
                  </div>
                ))}
              </div>
            ) : products?.length === 0 ? (
              <div className="bs-no-results">{t('bestSeller.noResults')}</div>
            ) : (
              <div className="bs-grid">
                {products?.map(product => (
                  <div key={product.id} className="bs-grid-item">
                    <ProductCard product={product} showRatingAndPrice={false} />
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bs-pagination">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                  className="bs-page-btn"
                >
                  <ChevronLeft size={16} />
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    className={`bs-page-btn ${currentPage === i + 1 ? 'active' : ''}`}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                  className="bs-page-btn"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default BestSeller;
